import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import lodashSortBy from 'lodash/fp/sortBy';

import {
  PARTNER_TASK_STATUS_NEEDS_REVIEW,
  PARTNER_TASK_STATUS_SENT,
  PARTNER_TASK_STATUS_IN_PROGRESS,
  PARTNER_TASK_STATUS_COMPLETED,
  PARTNER_TASK_STATUS_CANCELLED,
} from 'common/partnerTaskConstants';

import { USER_ROLE_ADMIN } from 'common/userRoles';

import { SR_API_ACTION_PARTNER_TASK_UPDATE_STATUS } from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';
import PartnerTaskActions from 'client/actions/PartnerTask';

import SrCard from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import SrPartnerTaskListSection from 'client/components/SrPartnerTaskList/SrPartnerTaskListSection';
import SrDividerLine from 'client/components/SrDividerLine';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';

import './SrPartnerTaskList.less';

const MODAL_TYPE_COMPLETED = 'completed';
const MODAL_TYPE_ARCHIVED = 'archived';

const _groupPartnerTasks = (partnerTasks) =>
  partnerTasks.reduce(
    (memo, partnerTask) => {
      switch (partnerTask.status) {
        case PARTNER_TASK_STATUS_NEEDS_REVIEW: {
          memo.needsReviewTaskList.push(partnerTask);
          break;
        }
        case PARTNER_TASK_STATUS_SENT: {
          memo.notStartedTaskList.push(partnerTask);
          break;
        }
        case PARTNER_TASK_STATUS_IN_PROGRESS: {
          memo.inProgressTaskList.push(partnerTask);
          break;
        }
        case PARTNER_TASK_STATUS_COMPLETED: {
          memo.completedTaskList.push(partnerTask);
          break;
        }
        case PARTNER_TASK_STATUS_CANCELLED: {
          // Only count/show tasks that were sent to the partner at some point.
          if (partnerTask.sentAt) {
            memo.cancelledTaskList.push(partnerTask);
          }
          break;
        }
      }
      return memo;
    },
    {
      needsReviewTaskList: [],
      inProgressTaskList: [],
      notStartedTaskList: [],
      completedTaskList: [],
      cancelledTaskList: [],
    },
  );

const SrPartnerTaskList = ({ listingId, partnerId, currentUser, partnerTasks, dispatch }) => {
  const [modalOpened, setModalOpened] = useState(null);
  const updatePartnerTask = useCallback(
    ({ partnerTaskId, updates } = {}) => {
      if (!partnerTaskId) {
        dispatch(PartnerTaskActions.getList, {
          listingId,
          partnerId,
        });
        return;
      }
      dispatch(postWithState, {
        action: SR_API_ACTION_PARTNER_TASK_UPDATE_STATUS,
        payload: {
          partnerTaskId,
          updates,
        },
      }).then(() => {
        dispatch(PartnerTaskActions.getList, {
          listingId,
          partnerId,
        });
      });
    },
    [listingId, partnerId, dispatch],
  );

  if (!listingId && !partnerId) {
    return null;
  }

  if (!Array.isArray(partnerTasks)) {
    return <div className="loading-gif" />;
  }

  const onCloseModals = () => setModalOpened(null);

  const {
    needsReviewTaskList,
    inProgressTaskList,
    notStartedTaskList,
    completedTaskList,
    cancelledTaskList,
  } = _groupPartnerTasks(partnerTasks);

  const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);

  return (
    <SrCard maxWidth="100%">
      {isAdmin && (
        <SrBox paddingBottom="15px">
          <SrPartnerTaskListSection
            title={`Needs Admin Review (${needsReviewTaskList.length}) (hidden to the partner)`}
            expandLabel={!partnerId ? null : 'needing review'}
            titleColor="orange300"
            rowColorHex="#FFF"
            borderColorHex="#D0D4D6"
            partnerTaskList={needsReviewTaskList}
            updatePartnerTask={updatePartnerTask}
            showListingAddress={!!partnerId}
            currentUser={currentUser}
          />
        </SrBox>
      )}
      <SrBox paddingBottom="15px">
        <SrPartnerTaskListSection
          title={`In Progress (${inProgressTaskList.length})`}
          expandLabel={!partnerId ? null : 'in progress'}
          rowColorHex="#F0F2F3"
          partnerTaskList={inProgressTaskList}
          updatePartnerTask={updatePartnerTask}
          showListingAddress={!!partnerId}
          currentUser={currentUser}
        />
      </SrBox>
      <SrPartnerTaskListSection
        title={`Fix the Problem (${notStartedTaskList.length})`}
        expandLabel={!partnerId ? null : 'needing fixes'}
        partnerTaskList={notStartedTaskList}
        updatePartnerTask={updatePartnerTask}
        showListingAddress={!!partnerId}
        currentUser={currentUser}
      />
      <SrBox paddingTop="12px" height="20px">
        <SrDividerLine
          text={
            <div>
              <SrText
                styleName={completedTaskList.length ? 'link' : null}
                cursor={completedTaskList.length ? 'pointer' : null}
                font="greycliff"
                weight="800"
                size="14"
                color="greyMonochrome"
                onClick={
                  completedTaskList.length ? () => setModalOpened(MODAL_TYPE_COMPLETED) : null
                }
              >
                {`${completedTaskList.length} Completed`}
              </SrText>
              &nbsp;|&nbsp;
              <SrText
                styleName={cancelledTaskList.length ? 'link' : null}
                cursor={cancelledTaskList.length ? 'pointer' : null}
                font="greycliff"
                weight="800"
                color="greyMonochrome"
                size="14"
                onClick={
                  cancelledTaskList.length ? () => setModalOpened(MODAL_TYPE_ARCHIVED) : null
                }
              >
                {`${cancelledTaskList.length} Archived`}
              </SrText>
            </div>
          }
          textProps={{
            font: 'greycliff',
            size: '14',
            color: 'greyMonochrome',
            weight: '800',
          }}
          alignItems="flex-start"
          height="2px"
        />
      </SrBox>
      {modalOpened && (
        <SrDarkModal onClose={onCloseModals}>
          <SrModalContent onCloseIconClick={onCloseModals}>
            <SrPartnerTaskListSection
              title={modalOpened === MODAL_TYPE_ARCHIVED ? 'Archived Tasks' : 'Completed Tasks'}
              partnerTaskList={
                modalOpened === MODAL_TYPE_ARCHIVED
                  ? lodashSortBy('cancelledAt', cancelledTaskList).reverse()
                  : completedTaskList
              }
              updatePartnerTask={updatePartnerTask}
              showListingAddress={!!partnerId}
              currentUser={currentUser}
            />
          </SrModalContent>
        </SrDarkModal>
      )}
    </SrCard>
  );
};

SrPartnerTaskList.propTypes = {
  listingId: PropTypes.number,
  partnerId: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  partnerTasks: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
};

SrPartnerTaskList.displayName = 'SrPartnerTaskList';

export default SrPartnerTaskList;
